import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Button, CircularProgress, Slide, styled } from "@mui/material";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import {
  ClinicThemeColorPicker,
  ReactColor,
} from "fe-web-components/settings/themeCustomization/clinicThemeColorPicker";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import {
  MergedConfigurationSettings,
  PatientColors,
  ThemeSettings,
} from "@kahunmedical/common-configuration";
import { useConfigurationSettingsManager } from "fe-web-components/settings/common/useConfigurationSettingsManager";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { useUpdateClinicConfig } from "../hooks/useUpdateClinicConfig";
import { isPaletteColor } from "fe-web-components/settings/themeCustomization/colorExtrapulation /types";
import { interpolateColorsUsingHSL } from "fe-web-components/settings/themeCustomization/colorExtrapulation /hsl";

//region [[ Styles ]]

const ClinicThemeCustomizationView = styled("div")({
  display: "flex",
  flexFlow: "row",
  marginTop: "10px",
});

const PendingChangesContainer = styled("div")({
  margin: "5px",
  justifyContent: "center",
  alignItems: "center",
  background: "transparent",
  height: "0",
  width: "0",
  transition: "all 0.8s ease-out",
  visibility: "hidden",
  "&.show": {
    visibility: "visible",
    padding: "10px",
    height: "56px",
    border: "1px dashed orange",
    background: "bisque",
    width: "100%",
  },
});

const ClinicViewContainer = styled("div")({
  flex: 1,
  height: "calc(90vh - 30px)",
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  "& .chat-container.fullscreen": {
    position: "absolute",
    margin: "0 5px",
    width: "100%",
  },
  "& .kahun-container.fullscreen.open": {
    background: "transparent",
  },
});

const ColorsPickerArea = styled("div")({
  flex: 1,
  margin: "20px",
  display: "flex",
  flexFlow: "column",
  maxHeight: "80vh",
  ...scrollableStyle,
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface ClinicThemeCustomizationProps {
  clinicId: string;
  onSave?: (newValue) => void;
  defaultTheme?: any; // TODO: add a robust type for themes
}

//endregion [[ Props ]]

//region [[ Functions ]]
enum MainColors {
  Primary,
  Default,
  Secondary,
}

// Translates Primary to "--primary"
const mainColorToColorKey = (mainColor: MainColors | string) => {
  return `--${(mainColor as string).toLowerCase()}`;
};
// Object.keys also returns the number entries, filter them out
const enumKeys = (e_num) =>
  Object.keys(e_num).filter((eKey) => isNaN(Number(eKey)));
//endregion [[ Functions ]]

export const ClinicThemeCustomizationV2 = ({
  clinicId,
}: ClinicThemeCustomizationProps) => {
  const [loading, setLoading] = useState(true);

  const [showMore, setShowAll] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  const [stateColors, setStateColors] = useState<PatientColors>(null);

  const {
    config,
    createStoreKeyHandler,
    isReady,
    mergedConfiguration,
  } = useUpdateClinicConfig();

  const initialClinicData = mergedConfiguration;

  const settingsManager = useConfigurationSettingsManager(initialClinicData);

  const handleColorSelected = useCallback((color: ReactColor, key: string) => {
    const p1stContainer = document.getElementsByClassName(
      "kahun-main-layout-view",
    );
    for (let i = 0; i < p1stContainer.length; i++) {
      const container = p1stContainer[i] as HTMLElement;
      container.style.setProperty(key, color.hex);
    }
  }, []);

  useEffect(() => {
    if (!settingsManager.loading && isReady) {
      const theme = settingsManager.settings.makeConfigObjectForKey(
        ThemeSettings,
        "theme",
      );
      setStateColors(theme.colors);
      if ((window as any).initKahunPatient) {
        // If already loaded the widget just re-initilie the widget
        (window as any).initKahunPatient({
          open: true,
          clinicId,
          // Make sure we always get latest settings
          ignoreCache: true,
          // Make sure we don't persist on the conversation
          nonPersist: true,
        });
      } else {
        (window as any).PUBLIC_URL = "https://patient-dev.kahun.com";
        let body = document.body;
        let script = document.createElement("script");
        script.type = "text/javascript";
        script.src = (window as any).PUBLIC_URL + "/embed.js";
        body.appendChild(script);
      }
    }
    return () => {
      (window as any).removeKahunPatient?.();
    };
  }, [
    settingsManager.loading,
    settingsManager.settings,
    isReady,
    handleColorSelected,
    clinicId,
  ]);

  const createStateColorChangeHandler = useCallback(
    (colorKey: string) => (color: ReactColor) => {
      setIsDirty(true);
      setStateColors((currentColors) => ({
        ...currentColors,
        [colorKey]: color.hex,
      }));
      handleColorSelected(color, colorKey);
    },
    [setStateColors, handleColorSelected, setIsDirty],
  );

  const handleSaveThemeClicked = useCallback(() => {
    let newSettings = settingsManager.settings;
    Object.entries(stateColors).forEach(([colorName, colorValue]) => {
      if (colorName !== "") {
        newSettings = newSettings.withValue(
          `theme.colors.${colorName}`,
          colorValue,
        );
      }
    });
    console.log(newSettings);
    const newObjValue = newSettings.getNonDefaultSettings();
    const newUnifiedConfigString = {
      ...config[ClinicConfigKeys.UnifiedConfigString],
      ...newObjValue,
    };
    createStoreKeyHandler(ClinicConfigKeys.UnifiedConfigString)(
      newUnifiedConfigString,
    );
    // console.log("save clicked", newUnifiedConfigString, newObjValue);
    setIsDirty(false);
  }, [
    setIsDirty,
    stateColors,
    config,
    createStoreKeyHandler,
    settingsManager.settings,
  ]);

  const generateColorSpecFromMainColors = () => {
    // Create a palette based on the main colors
    const palette = Object.entries(stateColors)
      .filter(([k, v]) => isPaletteColor(k))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    // Create a scheme with none palette colors
    const theme = new MergedConfigurationSettings({}).makeConfigObjectForKey(
      ThemeSettings,
      "theme",
    );
    const scheme = Object.entries(theme.colors)
      .filter(([k, v]) => !isPaletteColor(k))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const newScheme = {
      ...interpolateColorsUsingHSL(palette, scheme),
      ...palette,
    } as PatientColors;

    Object.entries(newScheme).forEach(([colorName, colorValue]) => {
      handleColorSelected({ hex: colorValue }, colorName);
    });

    setStateColors(newScheme);
  };

  if (!isReady) return <ThreeDotsLoader />;

  return (
    <ClinicThemeCustomizationView>
      <ClinicViewContainer>
        {loading && <CircularProgress />}
        <div
          id={"kahun-patient"}
          // @ts-ignore
          settings={JSON.stringify({
            open: true,
            clinicId,
            ignoreCache: true,
            // Make sure we don't persist on the conversation
            nonPersist: true,
            navigationBar: true,
          })}
          data-window-alignment={"fullscreen"}
        />
      </ClinicViewContainer>
      <ColorsPickerArea className={"sketch-color-picker-container"}>
        <PendingChangesContainer className={isDirty ? "show" : ""}>
          You are currently viewing a preview, to apply your theme changes to
          your clinic please click
          <Button onClick={handleSaveThemeClicked}>Save</Button>
        </PendingChangesContainer>
        {enumKeys(MainColors)
          .map(mainColorToColorKey)
          .map((colorKey) => (
            <ClinicThemeColorPicker
              key={colorKey}
              colorKey={colorKey}
              color={stateColors?.[colorKey] || ""}
              onColorChange={createStateColorChangeHandler(colorKey)}
            />
          ))}
        <Button onClick={generateColorSpecFromMainColors}>
          Auto Generate Colors
        </Button>
        <Button onClick={() => setShowAll((o) => !o)}>
          Show {showMore ? "only main colors" : "all colors"}
        </Button>
        <Slide direction={"up"} in={showMore} mountOnEnter unmountOnExit>
          <div>
            <b>You can manually change the following colors:</b>
            {Object.keys(stateColors || {})
              ?.filter(
                (stateColorKey) =>
                  !enumKeys(MainColors).some(
                    (enumColorName) =>
                      mainColorToColorKey(enumColorName) === stateColorKey,
                  ),
              )
              .map((colorKey) => (
                <ClinicThemeColorPicker
                  key={colorKey}
                  colorKey={colorKey}
                  color={stateColors?.[colorKey] || ""}
                  onColorChange={createStateColorChangeHandler(colorKey)}
                />
              ))}
          </div>
        </Slide>
      </ColorsPickerArea>
    </ClinicThemeCustomizationView>
  );
};
